/**
 * UBL Hook for Actual Size Container
 *
 * @description 실측 사이즈 비교하기 UBL 로깅
 */
import { useUBL, useUBLContext } from '@common/log-manager';

interface PurchasedProductUBLParams {
  purchased_product_id: string;
}

interface SaveSizeUBLParams {
  is_body_info_exist: boolean;
  saved_option_list: Array<{ name: string; value_list: string[] }>;
}

export const useActualSizeUBL = () => {
  const sendUBL = useUBL();
  const ublContext = useUBLContext();

  const navigation = ublContext.navigation || 'product_detail';
  // ============ impression ============

  /**
   * 실측 사이즈 비교하기 노출
   */
  const sectionImpressionUBL = () => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      data: ublContext.data,
    });
  };

  /**
   * 실측 사이즈 엠티뷰 노출
   */
  const emptyViewImpressionUBL = () => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare_size_emptyview',
      object_id: 'subtab_size_compare_size_emptyview',
      data: ublContext.data,
    });
  };

  /**
   * 나의 구매내역 탭 > 최근 구매한 상품 노출
   */
  const myPurchasedImpressionUBL = ({ purchased_product_id }: PurchasedProductUBLParams) => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      object_type: 'catalog',
      object_id: 'size_compare_my_purchased',
      data: { ...ublContext.data, purchased_product_id },
    });
  };

  /**
   * 직접 입력한 사이즈 탭 > 사이즈 입력 페이지 노출
   */
  const sizeInputPageImpressionUBL = () => {
    sendUBL({
      category: 'impression',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare_size_input',
      object_id: 'size_compare_my_size_input',
      data: ublContext.data,
    });
  };

  // ============ click ============

  /**
   * 실측 사이즈 엠티뷰 '사이즈 비교하기' 클릭
   */
  const emptyViewClickUBL = () => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare_size_emptyview',
      object_id: 'subtab_size_compare_size_emptyview',
      data: ublContext.data,
    });
  };

  /**
   * 나의 구매내역 탭 > 최근 구매한 상품 선택 (비교하기 클릭)
   */
  const myPurchasedClickUBL = ({ purchased_product_id }: PurchasedProductUBLParams) => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      object_type: 'catalog',
      object_id: 'size_compare_my_purchased',
      data: { ...ublContext.data, purchased_product_id },
    });
  };

  /**
   * 나의 구매내역 탭 클릭
   */
  const myPurchasedTabClickUBL = () => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      object_type: 'button',
      object_id: 'size_compare_my_purchased_tab',
      data: ublContext.data,
    });
  };

  /**
   * 직접 입력한 사이즈 탭 > '사이즈 입력하고 비교하기' 클릭
   */
  const mySizeInputButtonClickUBL = () => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      object_type: 'button',
      object_id: 'size_compare_my_size_btn',
      data: ublContext.data,
    });
  };

  /**
   * 직접 입력한 사이즈 탭 클릭
   */
  const mySizeTabClickUBL = () => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare',
      object_type: 'button',
      object_id: 'size_compare_my_size_tab',
      data: ublContext.data,
    });
  };

  /**
   * 직접 입력한 사이즈 탭 > 사이즈 입력 페이지 > 사이즈 저장하기
   */
  const saveSizeClickUBL = ({ is_body_info_exist, saved_option_list }: SaveSizeUBLParams) => {
    sendUBL({
      category: 'click',
      navigation,
      navigation_sub: ublContext.navigation_sub,
      object_section: 'subtab_size_compare_size_input',
      object_id: 'size_compare_my_size_input',
      data: { ...ublContext.data, is_body_info_exist, saved_option_list },
    });
  };

  return {
    // impression (알파벳 순)
    emptyViewImpressionUBL,
    myPurchasedImpressionUBL,
    sectionImpressionUBL,
    sizeInputPageImpressionUBL,
    // click (알파벳 순)
    emptyViewClickUBL,
    myPurchasedClickUBL,
    myPurchasedTabClickUBL,
    mySizeInputButtonClickUBL,
    mySizeTabClickUBL,
    saveSizeClickUBL,
  };
};
